import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import React from 'react';
import { GlobalConfiguration } from '../../../models/GlobalConfiguration';
import { StyledButton } from '../../styles/StyledButtons';
import { StyledHeading } from '../../styles/styles';
import { DOCUMENT_CATEGORY_FIELDS, GLOBAL_CONFIG_FIELD_LABELS } from './globalConfigFieldConfig';
import { getFieldDisplayValue } from './globalConfigAdminUtils';

type UnsavedChangesPanelProps = {
  dirtyFields: Set<string>;
  config: GlobalConfiguration;
  originalConfig: GlobalConfiguration;
  /** External id → name, so category fields show names rather than ids in the diff. */
  categoryNameById?: Record<string, string>;
  onRevertField: (field: string) => void;
};

const CATEGORY_FIELDS = new Set<string>(DOCUMENT_CATEGORY_FIELDS);

export default function UnsavedChangesPanel({
  dirtyFields,
  config,
  originalConfig,
  categoryNameById = {},
  onRevertField,
}: UnsavedChangesPanelProps) {
  if (dirtyFields.size === 0) {
    return null;
  }

  const displayValue = (field: string, value: unknown): string => {
    if (CATEGORY_FIELDS.has(field) && typeof value === 'string' && categoryNameById[value]) {
      return categoryNameById[value];
    }
    return getFieldDisplayValue(value);
  };

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        flex: 1,
        backgroundColor: 'background.paper',
      }}
    >
      <StyledHeading tone="tone1" headerVariant="subheading" reduceBottomPadding>
        Unsaved Changes
      </StyledHeading>
      <TableContainer component={Paper} elevation={0}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Field</TableCell>
              <TableCell>Change</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from(dirtyFields).map((field) => (
              <TableRow key={field}>
                <TableCell>{GLOBAL_CONFIG_FIELD_LABELS[field] || field}</TableCell>
                <TableCell>
                  {displayValue(
                    field,
                    (originalConfig as unknown as Record<string, unknown>)[field],
                  )}{' '}
                  → {displayValue(field, (config as unknown as Record<string, unknown>)[field])}
                </TableCell>
                <TableCell align="right">
                  <StyledButton
                    size="small"
                    variant="outlined"
                    onClick={() => onRevertField(field)}
                    sx={{ px: 1.5, py: 0.25, fontSize: '0.75rem' }}
                  >
                    Revert
                  </StyledButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
